import {type CreateExtArgs, pointerUtils} from '@wix/document-manager-core'
import type {CompRef, Pointer, VariantPointer, NestedComponentData} from '@wix/document-services-types'
import _ from 'lodash'
import {COMP_TYPES, VARIANTABLE_DATA_TYPES, VIEW_MODES} from '../../constants/constants'
import type {StructureExtensionAPI} from '../structure'
import type {LayoutExtensionApi} from '../layout/layout'
import type {VariantsExtensionAPI} from '../variants/variants'
import {SECTION_COMP_TYPES} from './constants'
import type {FiltersAPI} from './filters'
import type {DependenciesCollector, ExtractorsAPI} from './extractors'
import {SkipSectionError, type DataValidatorFn} from './validations'

export const createTraversal = (
    {pointers, extensionAPI}: CreateExtArgs,
    filters: FiltersAPI,
    extractors: ExtractorsAPI
) => {
    const structureApi = () => extensionAPI as StructureExtensionAPI

    const getComponentsToTraverse = (
        pagePointer: CompRef,
        includeSharedParts = false,
        isMasterPage = false
    ): CompRef[] => {
        const pageChildren = pointers.structure.getChildren(pagePointer)
        const {components} = structureApi()
        const layoutApi = extensionAPI as LayoutExtensionApi

        return _.uniqBy(
            pageChildren.reduce((compRefs: Pointer[], child: Pointer) => {
                const compType = components.getComponentType(child)
                const compTypes = isMasterPage
                    ? [COMP_TYPES.RESPONSIVE_SECTION, COMP_TYPES.HEADER_SECTION, COMP_TYPES.FOOTER_SECTION]
                    : [COMP_TYPES.RESPONSIVE_SECTION]

                if (compTypes.includes(compType)) {
                    compRefs.push(child)
                }

                const referredComp = pointers.structure.getSharedBlockFromInstance(child as CompRef)

                if (
                    includeSharedParts &&
                    referredComp &&
                    SECTION_COMP_TYPES.has(components.getComponentType(referredComp))
                ) {
                    compRefs.push(referredComp)
                } else if (!referredComp && layoutApi.components.isPinned(child)) {
                    compRefs.push(child)
                }

                return compRefs
            }, []) as CompRef[],
            'id'
        )
    }

    const buildNestedTreeInternal = (
        compPointer: CompRef,
        namespaces: string[],
        collector: DependenciesCollector,
        targetVariants: VariantPointer[] = [],
        validate?: DataValidatorFn,
        isRoot: boolean = true
    ): NestedComponentData | null => {
        if (filters.shouldExcludeComponent({compPointer})) {
            return null
        }

        const data = extractors.extractData(compPointer, namespaces, collector, targetVariants)

        const validationResult = validate?.(data, compPointer)
        if (validationResult === 'skip_section') throw new SkipSectionError(compPointer.id)
        if (validationResult === 'skip_component') return null

        const children = data.components

        const nestedChildren: NestedComponentData[] = children
            .map((childPointer: CompRef) => {
                return buildNestedTreeInternal(childPointer, namespaces, collector, targetVariants, validate, false)
            })
            .filter((child): child is NestedComponentData => child !== null)

        const result: NestedComponentData = {
            id: compPointer.id,
            parent: data.parent,
            semanticType: data.semanticType,
            ..._.omit(data, 'components', 'parent', 'semanticType', 'siteWidth'),
            components: nestedChildren
        }

        if (isRoot) {
            delete result.parent
        }

        if (data.siteWidth !== undefined) {
            result.siteWidth = data.siteWidth
        }

        return result
    }

    const buildNestedTree = (
        compPointer: CompRef,
        namespaces: string[],
        collector: DependenciesCollector,
        targetVariants: VariantPointer[] = [],
        validate?: DataValidatorFn,
        isRoot: boolean = true
    ): NestedComponentData | null => {
        try {
            return buildNestedTreeInternal(compPointer, namespaces, collector, targetVariants, validate, isRoot)
        } catch (error) {
            if (error instanceof SkipSectionError) {
                return null
            }
            throw error
        }
    }

    const buildNestedDesktopTree = (
        compPointer: CompRef,
        namespaces: string[],
        collector: DependenciesCollector,
        validate?: DataValidatorFn
    ): NestedComponentData | null => buildNestedTree(compPointer, namespaces, collector, [], validate, true)

    const buildNestedBreakpointTree = (
        compPointer: CompRef,
        targetVariants: VariantPointer[],
        namespaces: string[],
        collector: DependenciesCollector,
        validateData?: DataValidatorFn
    ): NestedComponentData | null =>
        buildNestedTree(compPointer, namespaces, collector, targetVariants, validateData, true)

    const variantsApi = () => (extensionAPI as VariantsExtensionAPI).variants

    const applyNestedTree = (node: NestedComponentData, targetBreakpoint: VariantPointer): void => {
        const compPointer = pointerUtils.getPointer(node.id, VIEW_MODES.DESKTOP) as CompRef
        const compPointerWithVariants = pointers.components.getComponentVariantsPointer(compPointer, [targetBreakpoint])
        for (const namespace of Object.values(VARIANTABLE_DATA_TYPES)) {
            const value = node[namespace]
            if (value) {
                variantsApi().updateComponentDataConsideringVariants(compPointerWithVariants, value, namespace)
            }
        }
        for (const child of node.components) {
            applyNestedTree(child, targetBreakpoint)
        }
    }

    return {
        getComponentsToTraverse,
        buildNestedDesktopTree,
        buildNestedBreakpointTree,
        applyNestedTree
    }
}
